import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Calendar,
  Clock,
  User,
  ArrowLeft,
  Eye,
  Tag,
  Share2,
  Twitter,
  Facebook,
  Linkedin,
  BookOpen
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { toast } from 'sonner';
import { Helmet } from 'react-helmet-async';

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  author: string;
  category: string;
  tags: string[];
  featured: boolean;
  published: boolean;
  read_time: number;
  meta_title?: string;
  meta_description?: string;
  og_image_url?: string;
  created_at: string;
  updated_at: string;
  published_at?: string;
  view_count: number;
}

const BlogDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [relatedPosts, setRelatedPosts] = useState<BlogPost[]>([]);

  useEffect(() => {
    if (slug) {
      fetchPost(slug);
    }
  }, [slug]);

  const fetchPost = async (postSlug: string) => {
    try {
      const response = await fetch(`/api/blog/posts/${postSlug}`);
      if (response.ok) {
        const data = await response.json();
        setPost(data);
        // Fetch related posts from same category
        fetchRelatedPosts(data.category, data.id);
      } else if (response.status === 404) {
        toast.error('Blog post not found');
        navigate('/blog');
      } else {
        toast.error('Failed to load blog post');
      }
    } catch (error) {
      console.error('Error fetching post:', error);
      toast.error('Failed to load blog post');
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedPosts = async (category: string, currentPostId: string) => {
    try {
      const response = await fetch(`/api/blog/posts?category=${category}&limit=3&published=true`);
      if (response.ok) {
        const data = await response.json();
        // Filter out current post and limit to 3
        const filtered = data
          .filter((p: BlogPost) => p.id !== currentPostId)
          .slice(0, 3);
        setRelatedPosts(filtered);
      }
    } catch (error) {
      console.error('Error fetching related posts:', error);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const sharePost = (platform: string) => {
    if (!post) return;
    
    const url = window.location.href;
    const title = post.title;
    
    switch (platform) {
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?url=${url}&text=${title}`, '_blank');
        break;
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank');
        break;
      case 'linkedin':
        window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, '_blank');
        break;
      case 'copy':
        navigator.clipboard.writeText(url);
        toast.success('Link copied to clipboard!');
        break;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1f4ed1] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-dineai-yellow"></div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-[#1f4ed1]">
        <Header />
        <div className="container mx-auto px-4 py-32 text-center">
          <h1 className="text-3xl font-bold text-white mb-4">Post Not Found</h1>
          <Link to="/blog">
            <Button className="bg-dineai-yellow hover:bg-dineai-yellow/90 text-black">
              Back to Blog
            </Button>
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{post.meta_title || post.title} | Dine AI Blog</title>
        <meta name="description" content={post.meta_description || post.excerpt} />
        <meta property="og:title" content={post.meta_title || post.title} />
        <meta property="og:description" content={post.meta_description || post.excerpt} />
        {post.og_image_url && <meta property="og:image" content={post.og_image_url} />}
        <meta property="og:url" content={window.location.href} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.meta_title || post.title} />
        <meta name="twitter:description" content={post.meta_description || post.excerpt} />
        {post.og_image_url && <meta name="twitter:image" content={post.og_image_url} />}
      </Helmet>

      <div className="min-h-screen bg-[#1f4ed1]">
        <Header />
        
        {/* Hero Section */}
        <section className="relative bg-gradient-to-r from-[#2256e5] to-[#1f4ed1] pt-32 pb-16 overflow-hidden">
          {/* Background Image Overlay */}
          {post.og_image_url && (
            <div 
              className="absolute inset-0 opacity-20"
              style={{
                backgroundImage: `url(${post.og_image_url})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                filter: 'blur(20px)'
              }}
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-black/10 to-transparent" />
          
          <div className="container mx-auto px-4 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="max-w-4xl mx-auto"
            >
              {/* Breadcrumb */}
              <div className="flex items-center gap-2 text-white/70 mb-6">
                <Link to="/blog" className="hover:text-white transition-colors">
                  Blog
                </Link>
                <span>/</span>
                <span className="text-white">{post.category}</span>
              </div>
              
              {/* Category & Featured Badge */}
              <div className="flex items-center gap-3 mb-4">
                <Badge className="bg-dineai-yellow text-black px-3 py-1">
                  {post.category}
                </Badge>
                {post.featured && (
                  <Badge className="bg-white/20 text-white px-3 py-1">
                    Featured
                  </Badge>
                )}
              </div>
              
              {/* Title */}
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
                {post.title}
              </h1>
              
              {/* Excerpt */}
              <p className="text-xl text-blue-100 mb-8">
                {post.excerpt}
              </p>
              
              {/* Meta Information */}
              <div className="flex flex-wrap items-center gap-6 text-white/80">
                <div className="flex items-center gap-2">
                  <User className="w-5 h-5" />
                  <span>{post.author}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  <span>{formatDate(post.published_at || post.created_at)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  <span>{post.read_time} min read</span>
                </div>
                <div className="flex items-center gap-2">
                  <Eye className="w-5 h-5" />
                  <span>{post.view_count} views</span>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
        
        {/* Content Section */}
        <section className="py-12 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              {/* Share Buttons - Top */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="flex items-center justify-between mb-8 pb-4 border-b"
              >
                <div className="flex items-center gap-4">
                  <span className="text-gray-600 text-sm font-medium">Share:</span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => sharePost('twitter')}
                      className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                      title="Share on Twitter"
                    >
                      <Twitter className="w-5 h-5 text-gray-600 hover:text-[#1DA1F2]" />
                    </button>
                    <button
                      onClick={() => sharePost('facebook')}
                      className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                      title="Share on Facebook"
                    >
                      <Facebook className="w-5 h-5 text-gray-600 hover:text-[#1877F2]" />
                    </button>
                    <button
                      onClick={() => sharePost('linkedin')}
                      className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                      title="Share on LinkedIn"
                    >
                      <Linkedin className="w-5 h-5 text-gray-600 hover:text-[#0A66C2]" />
                    </button>
                    <button
                      onClick={() => sharePost('copy')}
                      className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                      title="Copy Link"
                    >
                      <Share2 className="w-5 h-5 text-gray-600 hover:text-gray-900" />
                    </button>
                  </div>
                </div>
                <div className="text-sm text-gray-500">
                  {post.read_time} min read • {post.view_count} views
                </div>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                {/* Main Content */}
                <article className="prose prose-lg max-w-none">
                  {/* Featured Image if exists */}
                  {post.og_image_url && (
                    <img 
                      src={post.og_image_url} 
                      alt={post.title}
                      className="w-full rounded-lg mb-8 max-h-[500px] object-cover"
                    />
                  )}
                  
                  <div 
                    className="blog-content"
                    dangerouslySetInnerHTML={{ __html: post.content }}
                  />
                  
                  {/* Tags */}
                  {post.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-12 pt-8 border-t">
                      <Tag className="w-5 h-5 text-gray-600" />
                      {post.tags.map((tag) => (
                        <Badge key={tag} variant="outline" className="px-3 py-1">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  )}
                </article>
              </motion.div>
              
              {/* Related Posts */}
              {relatedPosts.length > 0 && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                  className="mt-16 pt-8 border-t"
                >
                  <h3 className="text-2xl font-bold text-gray-900 mb-8">Related Articles</h3>
                  <div className="grid md:grid-cols-3 gap-6">
                    {relatedPosts.map((relatedPost) => (
                      <Link
                        key={relatedPost.id}
                        to={`/blog/${relatedPost.slug}`}
                        className="group bg-gray-50 rounded-lg p-6 hover:bg-gray-100 transition-colors"
                      >
                        <h4 className="font-semibold text-lg text-gray-900 group-hover:text-[#1f4ed1] transition-colors line-clamp-2 mb-2">
                          {relatedPost.title}
                        </h4>
                        <p className="text-gray-600 line-clamp-3 mb-3">
                          {relatedPost.excerpt}
                        </p>
                        <div className="flex items-center gap-4 text-sm text-gray-500">
                          <span>{relatedPost.read_time} min read</span>
                          <span>•</span>
                          <span>{relatedPost.category}</span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </motion.div>
              )}
              
              {/* Back to Blog Button */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.5 }}
                className="mt-12 text-center"
              >
                <Link to="/blog">
                  <Button variant="outline" size="lg" className="gap-2">
                    <ArrowLeft className="w-4 h-4" />
                    Back to All Articles
                  </Button>
                </Link>
              </motion.div>
            </div>
          </div>
        </section>
        
        <Footer />
      </div>
      
      <style>{`
        .blog-content h1,
        .blog-content h2,
        .blog-content h3,
        .blog-content h4,
        .blog-content h5,
        .blog-content h6 {
          margin-top: 1.5rem;
          margin-bottom: 0.75rem;
          font-weight: bold;
          color: #1f2937;
        }
        
        .blog-content h2 {
          font-size: 1.75rem;
          border-bottom: 1px solid #e5e7eb;
          padding-bottom: 0.25rem;
          margin-top: 2rem;
        }
        
        .blog-content h3 {
          font-size: 1.5rem;
        }
        
        .blog-content p {
          margin-bottom: 1rem;
          line-height: 1.7;
          font-size: 1rem;
          color: #374151;
        }
        
        .blog-content strong {
          font-weight: 600;
          color: #111827;
        }
        
        .blog-content ul,
        .blog-content ol {
          margin-bottom: 1.5rem;
          padding-left: 2rem;
        }
        
        .blog-content li {
          margin-bottom: 0.5rem;
        }
        
        .blog-content strong {
          font-weight: 600;
        }
        
        .blog-content a {
          color: #1f4ed1;
          text-decoration: underline;
        }
        
        .blog-content a:hover {
          color: #2256e5;
        }
        
        .blog-content blockquote {
          border-left: 4px solid #FDB913;
          padding-left: 1rem;
          margin: 1.5rem 0;
          font-style: italic;
          color: #4b5563;
        }
        
        .blog-content code {
          background: #f3f4f6;
          padding: 0.125rem 0.25rem;
          border-radius: 0.25rem;
          font-family: monospace;
        }
        
        .blog-content pre {
          background: #1f2937;
          color: white;
          padding: 1rem;
          border-radius: 0.5rem;
          overflow-x: auto;
          margin: 1.5rem 0;
        }
        
        .blog-content pre code {
          background: transparent;
          padding: 0;
        }
        
        .blog-content img {
          max-width: 100%;
          height: auto;
          border-radius: 0.5rem;
          margin: 2rem 0;
        }
        
        .blog-content hr {
          margin: 2rem 0;
          border-top: 1px solid #e5e7eb;
        }
      `}</style>
    </>
  );
};

export default BlogDetail;